import React, { useCallback, useState } from 'react'
import { IconX } from '@tabler/icons-react'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { TextInput } from '@/components/forms/TextInput'
import { Loader } from '@/components/loaders/Loader'

import { FilterColumnButton } from '../FilterColumnButton'
import { FilterPill } from '../FilterPill'
import { FilterTypePopup } from './FilterTypePopup'

export const FilterTypeText = ({
  schema = {},
  value,
  loading,
  updateSearch,
  columnAccessor,
  onOpen,
}) => {
  const [open, setOpen] = useState(false)

  // Announce the open transition so callers can start slow work (e.g. bulk-loading
  // summaries) while the user is still deciding what to type.
  const handleSetOpen = (nextOpen) => {
    if (nextOpen && !open && onOpen) {
      onOpen(schema.accessor)
    }
    setOpen(nextOpen)
  }

  // The popup only mounts this input once it opens, and the tooltip mounts its
  // content a tick later than `open` flips — so focus on the input's own mount
  // via a callback ref rather than an effect keyed on `open` (which would run
  // before the input exists). Stable identity so it only fires on mount/unmount.
  const focusOnMount = useCallback((node) => {
    if (node) {
      node.focus({ preventScroll: true })
    }
  }, [])

  return (
    <FilterTypePopup
      open={open}
      setOpen={handleSetOpen}
      position={columnAccessor ? 'top left' : 'bottom left'}
      trigger={
        columnAccessor ? (
          <FilterColumnButton
            columnAccessor={columnAccessor}
            value={value}
            hasValue={!isEmpty(value)}
            displayLabel={schema.label}
          />
        ) : (
          <FilterPill
            clickable
            isClearable={!isEmpty(value)}
            handleRemoveFilter={(event) => {
              event.stopPropagation()

              updateSearch(schema.accessor, '')
            }}
            loading={loading}
            label={schema.label}
            value={value}
            active={open}
          />
        )
      }
    >
      <div className="flex-align-center small-gap inner-input-button">
        <TextInput
          ref={focusOnMount}
          type="text"
          data-testid="filter-type-text-input"
          className="full-width"
          placeholder={`Search ${schema.label}`}
          value={value || ''}
          onChange={(event) => {
            updateSearch(schema.accessor, event.target.value)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setOpen(false)
            }
          }}
        />
        <Button
          square
          aria-label="Clear search"
          basic
          onClick={() => {
            updateSearch(schema.accessor, '')
            setOpen(false)
          }}
        >
          <IconX />
        </Button>
      </div>
      {loading && columnAccessor && (
        <div className="flex-align-center small-gap filter-type-loading">
          <Loader size="small" data-testid="filter-type-loading" />
          <span>Loading all results to search…</span>
        </div>
      )}
    </FilterTypePopup>
  )
}
